import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/authContext.tsx";
import { ModuleId } from "../../types/session.ts";
import { ThemeSwitcher } from "../ui/themeSwitcher.tsx";
import { StatusIcon } from "../ui/statusIcon.tsx";
import { RevealCharsControls } from "../ui/revealCharsControls.tsx";
import { useDensity } from "../../hooks/useDensity.ts";
import { useFilterBarMode } from "../../hooks/useFilterBarMode.ts";
import { useFilterDisplay } from "../../hooks/useFilterDisplay.ts";
import {
  isSessionStateEnabled,
  setSessionStateEnabled,
  isPageStateEnabled,
  setPageStateEnabledSetting,
} from "../../hooks/useFilterUrlSync.ts";
import { PAGE_SIZE_OPTIONS, getDefaultPageSize, setDefaultPageSize } from "../../hooks/useGridData.ts";
import { ENDPOINT_DASHBOARD_MAP } from "../../pages/classicDashboard/classicDashboardPage.tsx";
import styles from "./userMenu.module.scss";

export type StartPage = "old" | "new";

function startPageKey(uid: number, endpointId: number): string {
  return `webadmincc-startpage_${uid}_${endpointId}`;
}

function startPageDefaultKey(uid: number): string {
  return `webadmincc-startpage-default_${uid}`;
}

export function getStartPage(uid?: number, endpointId?: number): StartPage {
  try {
    // 1. Per-user+endpoint override
    if (uid && endpointId) {
      const ep = localStorage.getItem(startPageKey(uid, endpointId));
      if (ep === "old" || ep === "new") return ep;
    }
    // 2. User default (set to whatever the user last chose)
    if (uid) {
      const def = localStorage.getItem(startPageDefaultKey(uid));
      if (def === "old" || def === "new") return def;
    }
  } catch { /* noop */ }
  return "new";
}

/**
 * Label for the Home / start-page menu link. Mirrors StartPageRoute's decision so
 * the menu names the dashboard that "/" actually opens — "Cockpit Dashboard" or
 * "Classic Dashboard" — instead of a generic "Dashboard" (which collides with the
 * Dashboard grid). Returns null for module / endpoint dashboard overrides, where
 * neither label fits and the menu's own DisplayName should stand.
 */
export function useStartPageLabel(): string | null {
  const { session, cache, canRead, isSuperAdmin, activeEndpoint } = useAuth();
  // Bespoke start pages (Instant Inventory module, per-endpoint instant dashboards)
  // — keep the menu's own label.
  if (activeEndpoint?.ModuleID === ModuleId.InstantInventory) return null;
  if (session?.ActiveEndPointID && ENDPOINT_DASHBOARD_MAP[session.ActiveEndPointID]) return null;
  const cockpitPage = cache?.PageInfo?.Page?.find(p => p.Name?.toLowerCase() === "cockpitdashboard");
  const hasCockpitAccess = isSuperAdmin || (cockpitPage ? canRead(cockpitPage.RowID) : false);
  const showCockpit = getStartPage(session?.UID, session?.ActiveEndPointID) === "new" && hasCockpitAccess;
  return showCockpit ? "Cockpit Dashboard" : "Classic Dashboard";
}

/** Persist the user's start-page preference without navigating. Callers decide whether to navigate/reload. */
export function setStartPagePreference(uid: number | undefined, endpointId: number | undefined, v: StartPage): void {
  try {
    if (uid && endpointId) localStorage.setItem(startPageKey(uid, endpointId), v);
    if (uid) localStorage.setItem(startPageDefaultKey(uid), v);
  } catch { /* noop */ }
}

type MenuView = "main" | "prefs";

export function UserMenu() {
  const { session, cache, canRead, isSuperAdmin, logout, switchEndpoint, clearCache, endpoints, activeEndpoint } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<MenuView>("main");
  const containerRef = useRef<HTMLDivElement>(null);
  const [sessionStateOn, setSessionStateOn] = useState(isSessionStateEnabled);
  const [pageStateOn, setPageStateOn] = useState(isPageStateEnabled);
  const [defaultRows, setDefaultRows] = useState(getDefaultPageSize);
  const { density, setDensity } = useDensity();
  const [filterBarMode, setFilterBarModeValue] = useFilterBarMode();
  const [filterDisplay, setFilterDisplayValue] = useFilterDisplay();
  const activeEp = activeEndpoint;
  // Endpoints load async after mount — show Switch until we've confirmed a single-endpoint user
  // (endpoints.length === 1). Otherwise it would disappear on page refresh.
  const isMulti = endpoints.length !== 1;

  const rawUser = cache?.UserInfo?.User ?? activeEp?.Title ?? "Menu";
  const userName = rawUser.charAt(0).toUpperCase() + rawUser.slice(1);

  const uid = session?.UID ?? 0;
  const epId = session?.ActiveEndPointID ?? 0;
  const [startPage, setStartPageState] = useState<StartPage>(() => getStartPage(uid, epId));
  const setStartPage = useCallback((v: StartPage) => {
    setStartPageState(v);
    setStartPagePreference(uid, epId, v);
    navigate("/");
    setTimeout(() => window.location.reload(), 0);
  }, [navigate, uid, epId]);

  useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [open]);

  // Always come back to the main view when re-opening the menu.
  useEffect(() => {
    if (!open) setView("main");
  }, [open]);

  return (
    <div ref={containerRef} className={styles.wrap}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={styles.trigger}
      >
        {userName}
        <span className={styles.caret}>&#x25BE;</span>
      </button>

      {open && view === "main" && (
        <div className={styles.dropdown}>
          {isMulti && (
            <MenuItem
              label="Switch endpoint"
              onClick={() => {
                setOpen(false);
                switchEndpoint();
              }}
            />
          )}
          <MenuItem
            label="Edit profile"
            onClick={() => {
              setOpen(false);
              navigate("/page/editProfile");
            }}
          />
          <MenuItem
            label="Clear cache"
            onClick={() => {
              setOpen(false);
              clearCache();
            }}
          />
          <div className={styles.divider} />
          <div className={styles.menuItem} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span>Theme</span>
            <ThemeSwitcher />
          </div>
          <div
            className={styles.menuItem}
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
            onClick={() => setView("prefs")}
            role="menuitem"
            aria-haspopup="menu"
            title="Open preferences"
          >
            <span>Preferences…</span>
            <span
              aria-hidden="true"
              style={{ display: "inline-flex", alignItems: "center", gap: "var(--space-1)", color: "var(--color-text-muted)", fontSize: "var(--text-xs)" }}
            >
              <span>More</span>
              <StatusIcon kind="chevronRight" size={10} />
            </span>
          </div>
          <div className={styles.divider} />
          <MenuItem
            label="Logout"
            onClick={() => {
              setOpen(false);
              logout();
            }}
          />
        </div>
      )}

      {open && view === "prefs" && (
        <div className={styles.prefsDropdown}>
          <div
            className={styles.menuItem}
            style={{ display: "flex", alignItems: "center", gap: "var(--space-1)", cursor: "pointer", color: "var(--color-text-secondary)", fontSize: "var(--text-xs)" }}
            onClick={() => setView("main")}
            role="menuitem"
            title="Back to menu"
          >
            <StatusIcon kind="chevronLeft" size={10} />
            <span>Back to menu</span>
            <span style={{ marginLeft: "auto", color: "var(--color-text-muted)", fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" }}>Preferences</span>
          </div>
          <div className={styles.divider} />
          <div
            className={styles.menuItem}
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "default" }}
            title={[
              "Comfortable — roomy spacing for occasional users.",
              "Compact     — tighter spacing for daily operators.",
              "Dense       — minimal spacing for power users.",
            ].join("\n")}
          >
            <span>Density</span>
            <select
              value={density}
              onChange={e => setDensity(e.target.value as "comfortable" | "compact" | "dense")}
              style={{ fontSize: "var(--text-xs)", padding: "var(--space-0-5) var(--space-1)", borderRadius: "var(--radius-1)", border: "1px solid var(--color-border-default)", background: "var(--color-bg)", color: "var(--color-text)" }}
            >
              <option value="comfortable">Comfortable</option>
              <option value="compact">Compact</option>
              <option value="dense">Dense</option>
            </select>
          </div>
          <div
            className={styles.menuItem}
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "default" }}
            title={[
              "Chips    — active filters as removable pills; edit via + Filter button.",
              "Classic  — always-visible filter bar with labelled inputs.",
            ].join("\n")}
          >
            <span>Filter bar</span>
            <select
              value={filterBarMode}
              onChange={e => setFilterBarModeValue(e.target.value as "chips" | "classic")}
              style={{ fontSize: "var(--text-xs)", padding: "var(--space-0-5) var(--space-1)", borderRadius: "var(--radius-1)", border: "1px solid var(--color-border-default)", background: "var(--color-bg)", color: "var(--color-text)" }}
            >
              <option value="chips">Chips (new)</option>
              <option value="classic">Classic</option>
            </select>
          </div>
          <div
            className={styles.menuItem}
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "default" }}
            title={[
              "How Kiosk / Location / Product filters render.",
              "",
              "• Compact pills     — one pill per filter; click to open.",
              "• Inline fields     — ID + search always visible.",
              "• Command palette   — one smart input (ID, search, #city:…).",
            ].join("\n")}
          >
            <span>Filter display</span>
            <select
              value={filterDisplay}
              onChange={e => setFilterDisplayValue(e.target.value as "compact" | "inline" | "palette")}
              style={{ fontSize: "var(--text-xs)", padding: "var(--space-0-5) var(--space-1)", borderRadius: "var(--radius-1)", border: "1px solid var(--color-border-default)", background: "var(--color-bg)", color: "var(--color-text)" }}
            >
              <option value="compact">Compact pills</option>
              <option value="inline">Inline fields</option>
              <option value="palette">Command palette</option>
            </select>
          </div>
          <RevealCharsControls rowClassName={styles.menuItem} />
          {activeEndpoint?.ModuleID !== ModuleId.InstantInventory && (() => {
            const cockpitPage = cache?.PageInfo?.Page?.find(p => p.Name?.toLowerCase() === "cockpitdashboard");
            const hasCockpitAccess = isSuperAdmin || (cockpitPage ? canRead(cockpitPage.RowID) : false);
            if (!hasCockpitAccess) return null;
            return (
          <div
            className={styles.menuItem}
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "default" }}
            title={[
              "Which page opens when you sign in or go Home.",
              "",
              "• Legacy — the familiar start page with menu links.",
              "• Cockpit — an overview dashboard with today's numbers and trends at a glance.",
              "",
              "Your choice is remembered per endpoint.",
            ].join("\n")}
          >
            <span>Start page</span>
            <select
              value={startPage}
              onChange={e => setStartPage(e.target.value as StartPage)}
              style={{ fontSize: "var(--text-xs)", padding: "var(--space-0-5) var(--space-1)", borderRadius: "var(--radius-1)", border: "1px solid var(--color-border-default)", background: "var(--color-bg)", color: "var(--color-text)" }}
            >
              <option value="old">Legacy (classic)</option>
              <option value="new">Cockpit (new)</option>
            </select>
          </div>
            );
          })()}
          <div
            className={styles.menuItem}
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "default" }}
            title="How many rows each report shows per page by default. You can still change it on individual reports."
          >
            <span>Default rows</span>
            <select
              value={defaultRows}
              onChange={e => {
                const v = Number(e.target.value);
                setDefaultPageSize(v);
                setDefaultRows(v);
              }}
              style={{ fontSize: "var(--text-xs)", padding: "var(--space-0-5) var(--space-1)", borderRadius: "var(--radius-1)", border: "1px solid var(--color-border-default)", background: "var(--color-bg)", color: "var(--color-text)" }}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>{size} rows</option>
              ))}
            </select>
          </div>
          <div
            className={styles.menuItem}
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "default" }}
            title={[
              "How your filters and sorting are remembered as you work.",
              "",
              "• ON (recommended) — your filters are kept privately in this browser tab. Reloading restores them, and the filter values never show up in the address bar or browser history. Better for privacy when the screen is shared or when filters contain sensitive values.",
              "• OFF — filters are added to the page address so you can copy the URL and share it with a colleague (they'll see the same view). The trade-off: filter values become part of the browser history.",
            ].join("\n")}
          >
            <span>Session state</span>
            <input
              type="checkbox"
              checked={sessionStateOn}
              onChange={e => {
                const v = e.target.checked;
                setSessionStateEnabled(v);
                setSessionStateOn(v);
                if (!v) setPageStateOn(isPageStateEnabled());
              }}
            />
          </div>
          <div
            className={styles.menuItem}
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "default", paddingLeft: "var(--space-4)", opacity: sessionStateOn ? 1 : 0.5 }}
            title={[
              "Works together with Session state (only available when that is on).",
              "",
              "• ON — each page keeps its own filters. Move to another page and come back — your earlier filters are still there.",
              "• OFF — filters only survive a reload. Switching between pages starts fresh each time.",
              "",
              "Not needed when Session state is off — each address already carries its own filters.",
            ].join("\n")}
          >
            <span>Per-page state</span>
            <input
              type="checkbox"
              checked={pageStateOn && sessionStateOn}
              disabled={!sessionStateOn}
              onChange={e => {
                const v = e.target.checked;
                setPageStateEnabledSetting(v);
                setPageStateOn(v);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function MenuItem({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <div
      onClick={onClick}
      className={styles.menuItem}
    >
      {label}
    </div>
  );
}
